import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Drop } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RefreshmentsPublic() {
  const [board, setBoard] = useState(null);
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/refreshments/public", { params: category ? { category } : {} })
      .then(({ data }) => setBoard(data))
      .catch(() => setBoard({ items: [], categories: [] }));
  }, [category]);

  useSeo({
    title: "Water, energy drinks and local coffee | Unity & Utility",
    description: (board?.blurb || "Water, sparkling water, soda, energy drinks, electrolytes, local coffee, paper towels and wipes for the crew.").slice(0, 180),
    path: "/refreshments",
    image: board?.picture ? `${BACKEND}${board.picture}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Drinks & supplies", "/refreshments"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="refreshments-public-page">
        <Crumbs items={[["Home", "/"], ["Drinks & supplies", "/refreshments"]]} />
        <p className="overline text-[#B3241B]">What the crew drinks</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Drop size={34} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          Drinks &amp; supplies
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="refreshments-blurb">
          {board?.blurb || "Water, sparkling water, soda, energy drinks, electrolytes and coffee from local roasters, plus paper towels, wipes, soap and cleaning supplies for the floor."}
        </p>
        <div className="mt-5">
          <ShareBar url={`${BACKEND}/api/share/refreshments`} text="Water, energy drinks and local coffee"
            testid="refreshments-public-share" instagram />
        </div>

        {board?.categories?.length > 0 && (
          <div className="scroll-x mt-8 flex gap-1.5 overflow-x-auto" data-testid="refreshments-public-filter">
            {[["", "Everything"], ...board.categories.map((c) => [c.key, c.label])].map(([key, label]) => (
              <button key={key || "all"} data-testid={`refreshments-public-category-${key || "all"}`} onClick={() => setCategory(key)}
                className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  category === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {board?.items?.length === 0 ? (
          <p className="mt-8 text-sm text-slate-500" data-testid="refreshments-public-empty">
            Nothing on the board for that filter just now.
          </p>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="refreshments-public-list">
            {board?.items?.map((d, i) => (
              <div key={d.id} data-testid={`refreshments-public-${i}`} className="border border-border bg-white">
                {d.image_url && (
                  <Link to={`/refreshments/${d.id}`}>
                    <img src={`${BACKEND}${d.image_url}`} alt={d.name} className="h-36 w-full object-cover" />
                  </Link>
                )}
                <div className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">
                    {d.category_label}{d.local ? " · local" : ""}
                  </p>
                  <Link to={`/refreshments/${d.id}`} className="mt-1 block font-semibold text-[#0F172A] hover:text-[#002FA7]">
                    {d.brand ? `${d.brand} ` : ""}{d.name}
                  </Link>
                  <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{d.symbol}{d.price}</p>
                  <div className="mt-2">
                    <ShareBar url={`${BACKEND}/api/share/drink/${d.id}`} text={`${d.brand || ""} ${d.name}`.trim()}
                      testid={`refreshments-public-share-${i}`} compact instagram />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

export function RefreshmentPublic() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/refreshments/public/${id}`).then(({ data }) => setD(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Not on the board"));
  }, [id]);

  useSeo({
    title: d ? `${d.brand ? `${d.brand} ` : ""}${d.name} | Unity & Utility` : "Drinks & supplies | Unity & Utility",
    description: (d?.description || d?.name || "From our crew board.").slice(0, 180),
    path: `/refreshments/${id}`,
    image: d?.image_url ? `${BACKEND}${d.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Drinks & supplies", "/refreshments"], [d?.name || "Item", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="refreshment-page">
        <Crumbs items={[["Home", "/"], ["Drinks & supplies", "/refreshments"], [d?.name || "Item", null]]} />
        {error && <p data-testid="refreshment-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {d && (
          <>
            <p className="overline text-[#B3241B]">{d.category_label}{d.local ? " · local" : ""}</p>
            <h1 data-testid="refreshment-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              {d.brand ? `${d.brand} ` : ""}{d.name}
            </h1>
            {d.image_url && (
              <img data-testid="refreshment-image" src={`${BACKEND}${d.image_url}`} alt={d.name}
                className="mt-6 w-full border border-border object-cover" />
            )}
            <p data-testid="refreshment-price" className="mt-5 text-2xl font-bold text-[#0B5C2E]">
              {d.symbol}{d.price}
              {d.size && <span className="ml-2 align-middle text-xs font-semibold uppercase tracking-wide text-slate-500">{d.size}</span>}
            </p>
            {d.description && (
              <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{d.description}</p>
            )}
            <div className="mt-6">
              <ShareBar url={`${BACKEND}/api/share/drink/${id}`} text={`${d.brand || ""} ${d.name}`.trim()}
                testid="refreshment-share" instagram />
            </div>
            <Link to="/refreshments" data-testid="refreshment-back"
              className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
              See the whole board
            </Link>
          </>
        )}
      </div>
    </PublicShell>
  );
}
